import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/core/api/apiTypes";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { formApiService } from "../services/formApiService";
import {
  fromCreateFormRequestToCreateFormDto,
  fromFormDtoToForm,
  fromUpdateFormRequestToUpdateFormDto,
  type CreateFormRequest,
  type Form,
  type UpdateFormRequest,
} from "../types/formTypes";

export const formKeys = {
  all: ["forms"] as const,
  business: (businessId?: string, userId?: string) => ["forms", businessId, userId] as const,
  list: (businessId?: string, userId?: string) => [...formKeys.business(businessId, userId), "list"] as const,
  detail: (businessId?: string, userId?: string, formId?: string) =>
    [...formKeys.business(businessId, userId), "detail", formId] as const,
};

function retryRead(count: number, error: Error) {
  return !(error instanceof ApiError && error.Status < 500) && count < 2;
}

function checkBusiness(form: Form, businessId: string, formId?: string) {
  if (form.businessId !== businessId || (formId && form.id !== formId)) {
    throw new Error("This form does not belong to the requested business.");
  }
  return form;
}

export function useForms(businessId?: string) {
  const { user, loading, getAuthGeneration } = useAuth();
  return useQuery({
    queryKey: formKeys.list(businessId, user?.id),
    queryFn: async ({ signal }) => {
      if (!businessId) throw new Error("A business is required to load forms.");
      const generation = getAuthGeneration();
      const dtos = await formApiService.getAll(businessId, signal);
      signal.throwIfAborted();
      if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
      return dtos.map(dto => checkBusiness(fromFormDtoToForm(dto), businessId));
    },
    enabled: !!businessId && !!user && !loading,
    staleTime: 0,
    retry: retryRead,
  });
}

export function useForm(businessId?: string, formId?: string) {
  const { user, loading, getAuthGeneration } = useAuth();
  return useQuery({
    queryKey: formKeys.detail(businessId, user?.id, formId),
    queryFn: async ({ signal }) => {
      if (!businessId || !formId) throw new Error("A business and form are required.");
      const generation = getAuthGeneration();
      const dto = await formApiService.getById(businessId, formId, signal);
      signal.throwIfAborted();
      if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
      return checkBusiness(fromFormDtoToForm(dto), businessId, formId);
    },
    enabled: !!businessId && !!formId && !!user && !loading,
    staleTime: 0,
    retry: retryRead,
  });
}

function useFormScope(businessId: string) {
  const { user, getAuthGeneration } = useAuth();
  const queryClient = useQueryClient();
  function capture() {
    if (!businessId || !user) throw new Error("Sign in to a business before changing forms.");
    const generation = getAuthGeneration();
    return {
      businessId, userId: user.id,
      check() {
        if (generation !== getAuthGeneration()) throw new Error("Your account changed. Reopen this form.");
      },
    };
  }
  async function invalidate(scope: ReturnType<typeof capture>, form?: Form, deletedId?: string) {
    scope.check();
    await queryClient.cancelQueries({ queryKey: formKeys.business(scope.businessId, scope.userId) });
    scope.check();
    if (form) {
      checkBusiness(form, scope.businessId);
    }
    if (deletedId) {
      queryClient.removeQueries({ queryKey: formKeys.detail(scope.businessId, scope.userId, deletedId), exact: true });
      queryClient.setQueryData<Form[]>(formKeys.list(scope.businessId, scope.userId),
        forms => forms?.filter(item => item.id !== deletedId));
    }
    // Refetch rather than installing a delayed save response over a newer save or deletion.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: formKeys.list(scope.businessId, scope.userId), exact: true }),
      ...(form ? [queryClient.invalidateQueries({
        queryKey: formKeys.detail(scope.businessId, scope.userId, form.id), exact: true,
      })] : []),
    ]);
    scope.check();
  }
  return { capture, invalidate };
}

export function useCreateForm(businessId: string) {
  const { capture, invalidate } = useFormScope(businessId);
  return useMutation({
    mutationFn: async (request: CreateFormRequest) => {
      const scope = capture();
      const form = fromFormDtoToForm(await formApiService.create(scope.businessId, fromCreateFormRequestToCreateFormDto(request)));
      await invalidate(scope, form);
      return form;
    },
    retry: false,
  });
}

export function useUpdateForm(businessId: string, formId: string) {
  const { capture, invalidate } = useFormScope(businessId);
  return useMutation({
    mutationFn: async (request: UpdateFormRequest) => {
      const scope = capture();
      const form = checkBusiness(fromFormDtoToForm(await formApiService.update(
        scope.businessId, formId, fromUpdateFormRequestToUpdateFormDto(request),
      )), scope.businessId, formId);
      await invalidate(scope, form);
      return form;
    },
    retry: false,
  });
}

export function useDeleteForm(businessId: string, formId: string) {
  const { capture, invalidate } = useFormScope(businessId);
  return useMutation({
    mutationFn: async () => {
      const scope = capture();
      await formApiService.delete(scope.businessId, formId);
      await invalidate(scope, undefined, formId);
    },
    retry: false,
  });
}
